import { createAction } from 'redux-starter-kit'
import { all, takeLatest, select, put, call } from 'redux-saga/effects'
import { createSelector } from 'reselect'
import { get, cloneDeep } from 'lodash'
import { appLanguages, questionType, roleuser } from '@edulastic/constants'
import {
  UPDATE_QUESTION,
  getCurrentQuestionSelector,
} from '../../../author/sharedDucks/questions'
import {
  getUserRole,
  getUserPreferredLanguage,
} from '../../../author/src/selectors/user'
import {
  clozeTypes,
  omitEmptyItemData,
  resetEmptyEnglishData,
  resetEmptyLanguageData,
} from '../../../assessment/utils/question'
import {
  getTestEntitySelector,
  SET_TEST_DATA,
} from '../../../author/TestPage/ducks'
import {
  omitEmptyTestData,
  resetEmptyTestEnglishData,
  resetEmptyTestLanguageData,
} from '../../../assessment/utils/test'

const { LANGUAGE_EN } = appLanguages
const SET_LANGUAGE = '[language] set language'
const SET_LANGUAGE_TABS = '[language] set language tabs'
export const setLangAction = createAction(SET_LANGUAGE)
export const setLangTabsAction = createAction(SET_LANGUAGE_TABS)

const initialState = {
  language: '',
  languageTabs: [LANGUAGE_EN],
}
export function languageReducer(state = initialState, { type, payload }) {
  switch (type) {
    case SET_LANGUAGE:
      return {
        ...state,
        language: payload.languageCode,
      }
    case SET_LANGUAGE_TABS:
      return {
        ...state,
        languageTabs: payload,
      }
    default:
      return state
  }
}

export default languageReducer

export const languageStateSelector = (state) => state.languages.language
export const getLanguageTabsSelector = (state) => state.languages.languageTabs
export const getUtaPeferredLanguage = (state) =>
  state.test.languagePreference || LANGUAGE_EN

export const getIsMultiLanguageEnabled = (state) => {
  const assignmentById = get(state, 'studentAssignment.byId')
  const currentAssignment = get(state, 'studentAssignment.current')
  const isPreview = get(state, 'test.isTestPreviewModalVisible', false)
  if (isPreview) {
    return get(state, 'test.previewState.multiLanguageEnabled', false)
  }
  return !!assignmentById[currentAssignment]?.multiLanguageEnabled
}
export const getCurrentLanguage = createSelector(
  languageStateSelector,
  getUserRole,
  getUserPreferredLanguage,
  getUtaPeferredLanguage,
  getIsMultiLanguageEnabled,
  (
    state,
    userRole,
    userPreferredLanguage,
    utaPeferredLanguage,
    isMultiLanguageEnabled
  ) => {
    if (userRole === roleuser.STUDENT) {
      if (isMultiLanguageEnabled)
        return utaPeferredLanguage || userPreferredLanguage || LANGUAGE_EN
      return LANGUAGE_EN
    }
    if (userRole !== roleuser.STUDENT && isMultiLanguageEnabled) {
      return utaPeferredLanguage || LANGUAGE_EN
    }
    return state || LANGUAGE_EN
  }
)

// used in LCB, ExpressGrader and student side grades
export const languagePreferenceSelector = (state, props = {}) => {
  let preferredLanguage = 'en'
  const {
    isLCBView,
    isStudentReport,
    isExpressGrader,
    data,
    testActivityId: utaId,
    utaPreferredLanguage,
  } = props || {}
  if (isLCBView || isExpressGrader) {
    const { activity: { testActivityId = utaId, userId } = {} } = data || {}
    const {
      testActivities: currentUTAS = [],
      recentTestActivitiesGrouped = {},
    } = state?.author_classboard_testActivity?.data || {}
    const previousUTAS = recentTestActivitiesGrouped?.[userId] || []
    const allUTAS = [...currentUTAS, ...previousUTAS]
    if (testActivityId) {
      const currentUTA = allUTAS.find((uta) => uta._id === testActivityId)
      const { languagePreference = 'en' } = currentUTA || {}
      preferredLanguage = languagePreference
    }
    if (
      utaPreferredLanguage &&
      [questionType.PASSAGE, questionType.VIDEO].includes(data?.type)
    ) {
      preferredLanguage = utaPreferredLanguage
    }
  } else if (isStudentReport) {
    const { languagePreference = 'en' } =
      state?.studentReport?.testActivity || {}
    preferredLanguage = languagePreference
  }

  return preferredLanguage
}

function* setTestItemLanguageSaga({
  languageCode,
  isSavingLanguageTabs,
  payload,
}) {
  const currentLanguage = yield select(languageStateSelector)
  let question = yield select(getCurrentQuestionSelector)
  const classResponse = yield select((state) => state.classResponse.data)
  const langauageTabs = yield select(getLanguageTabsSelector)
  if (currentLanguage?.length) {
    if (question && !Object.keys(classResponse).length) {
      const { newItem, omitted } = omitEmptyItemData(question, langauageTabs)
      question = newItem
      if (omitted) {
        yield put({
          type: UPDATE_QUESTION,
          payload: newItem,
        })
      }
    }
    if (question && languageCode !== LANGUAGE_EN) {
      const changedQuestion = resetEmptyLanguageData(question, languageCode)
      if (changedQuestion) {
        yield put({
          type: UPDATE_QUESTION,
          payload: changedQuestion,
        })
      }
    }
    if (
      question &&
      languageCode === LANGUAGE_EN &&
      clozeTypes.includes(question.type) &&
      isSavingLanguageTabs
    ) {
      const changedQuestion = resetEmptyEnglishData(question, languageCode)
      if (changedQuestion) {
        yield put({
          type: UPDATE_QUESTION,
          payload: changedQuestion,
        })
      }
    }
    if (
      question &&
      payload === LANGUAGE_EN &&
      clozeTypes.includes(question.type)
    ) {
      const changedQuestion = resetEmptyEnglishData(question, payload)
      if (changedQuestion) {
        yield put({
          type: UPDATE_QUESTION,
          payload: changedQuestion,
        })
      }
    }
  }
}

function* setTestInstructionLanguageSaga(payload) {
  const { languageCode, isSavingLanguageTabs } = payload
  const currentLanguage = yield select(languageStateSelector)
  let test = cloneDeep(yield select(getTestEntitySelector))

  const langauageTabs = yield select(getLanguageTabsSelector)
  if (currentLanguage?.length) {
    if (test) {
      const { newTest, omitted } = omitEmptyTestData(test, langauageTabs)
      test = newTest
      if (omitted) {
        yield put({
          type: SET_TEST_DATA,
          payload: { data: newTest },
        })
      }
    }
    if (test && languageCode !== LANGUAGE_EN) {
      const changedTest = resetEmptyTestLanguageData(test, languageCode)
      if (changedTest) {
        yield put({
          type: SET_TEST_DATA,
          payload: { data: changedTest },
        })
      }
    }
    if (test && languageCode === LANGUAGE_EN && isSavingLanguageTabs) {
      const changedTest = resetEmptyTestEnglishData(test, languageCode)
      if (changedTest) {
        yield put({
          type: SET_TEST_DATA,
          payload: { data: changedTest },
        })
      }
    }
  }
}

function* setLanguageSaga({ payload }) {
  const { isTestSettingsView } = payload || {}
  if (!isTestSettingsView) {
    yield call(setTestItemLanguageSaga, payload)
  }
  if (isTestSettingsView) {
    yield call(setTestInstructionLanguageSaga, payload)
  }
}

export function* languageSaga() {
  yield all([yield takeLatest(SET_LANGUAGE, setLanguageSaga)])
}
